import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type ReactNode } from 'react';
import {
  ExitFullscreenIcon,
  FullscreenIcon,
  PauseIcon,
  PlayIcon,
  SkipBackIcon,
  SkipForwardIcon,
} from '../components/icons';
import { cx } from '../components/ui';
import { timecode } from '../lib/format';
import { pieceId, piecesOf } from '../lib/timeline';
import { useHotkeys } from '../lib/useHotkeys';
import type { EditState } from '../queries/edit';
import { usePlanPlayer } from './usePlanPlayer';

/**
 * The edit, played before it has rendered.
 *
 * MediaConvert takes a minute or two to cut a new version, and until it lands
 * the share page has only the previous one to play, which is exactly what the
 * owner just changed. This plays what they will get instead: the original
 * straight from storage, piece by piece in the edit's order, with each
 * stitched-in clip played from its own recording. It is the editor's preview
 * in the player's clothes, through the same engine (usePlanPlayer) and from
 * the same list the render works from.
 */
export function EditPreview({
  state,
  title,
  posterUrl,
}: {
  state: EditState;
  title: string;
  posterUrl: string | null;
}) {
  const pieces = useMemo(() => piecesOf(state, state.sourceDurationSec), [state]);
  const sources = useMemo(
    () => new Map(state.clipSources.map((source) => [source.videoId, source])),
    [state.clipSources],
  );
  const player = usePlanPlayer({ pieces });
  const { plan, total, outTime, playing, activeClip, toggle, seekOut } = player;

  const containerRef = useRef<HTMLDivElement>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const hideTimerRef = useRef<number | null>(null);

  useEffect(() => {
    const onChange = () => setFullscreen(document.fullscreenElement !== null);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  useEffect(() => {
    return () => {
      if (hideTimerRef.current !== null) window.clearTimeout(hideTimerRef.current);
    };
  }, []);

  const seekBy = (delta: number) => seekOut(outTime + delta);

  const toggleFullscreen = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
    else void container.requestFullscreen().catch(() => {});
  }, []);

  useHotkeys({
    space: toggle,
    k: toggle,
    arrowleft: () => seekBy(-5),
    arrowright: () => seekBy(5),
    j: () => seekBy(-10),
    l: () => seekBy(10),
    f: toggleFullscreen,
  });

  const wake = useCallback(() => {
    setShowControls(true);
    if (hideTimerRef.current !== null) window.clearTimeout(hideTimerRef.current);
    hideTimerRef.current = window.setTimeout(() => {
      setShowControls(false);
    }, 2600);
  }, []);

  const onScrub = (event: ChangeEvent<HTMLInputElement>) => seekOut(Number(event.target.value));

  const progressPercent = total > 0 ? (outTime / total) * 100 : 0;
  const activeSource =
    activeClip && activeClip.piece.kind === 'clip' ? sources.get(activeClip.piece.videoId) : undefined;

  return (
    <div className="space-y-3">
      <div
        ref={containerRef}
        onMouseMove={wake}
        onTouchStart={wake}
        className="group relative overflow-hidden rounded-md border border-hairline bg-black"
      >
        <video
          ref={player.videoRef}
          src={state.sourceUrl}
          playsInline
          preload="metadata"
          poster={posterUrl ?? undefined}
          onClick={toggle}
          className="aspect-video w-full cursor-pointer bg-black"
        />

        {activeClip && activeClip.piece.kind === 'clip' ? (
          <div className="absolute inset-0 bg-black">
            <video
              ref={player.clipVideoRef}
              key={pieceId(activeClip.piece)}
              src={activeSource?.url ?? undefined}
              playsInline
              preload="auto"
              onClick={toggle}
              className="aspect-video h-full w-full cursor-pointer bg-black"
              {...player.clipHandlers}
            />
          </div>
        ) : null}

        {/* Says what this is, in the frame, so nobody mistakes it for the
            rendered file: the picture is the original at full size and the
            clips are fetched one by one, so it can stutter where the render
            will not. */}
        <span className="pointer-events-none absolute left-3 top-3 rounded bg-slate-950/80 px-2 py-1 font-mono text-micro uppercase tracking-wide text-chalk">
          Preview
        </span>

        {!playing ? (
          <button
            type="button"
            onClick={toggle}
            aria-label={`Play a preview of ${title}`}
            className="absolute inset-0 flex items-center justify-center bg-slate-950/25 transition-colors duration-120 hover:bg-slate-950/35"
          >
            <span className="brand-play flex h-16 w-16 items-center justify-center rounded-full">
              <PlayIcon size={26} />
            </span>
          </button>
        ) : null}

        <div
          className={cx(
            'absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/95 to-transparent px-3 pb-2.5 pt-8 transition-opacity duration-200',
            showControls || !playing ? 'opacity-100' : 'pointer-events-none opacity-0',
          )}
        >
          <div className="relative mb-2 h-1.5">
            <div className="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-chalk/20" />
            {/* Where the clips sit in the finished video. */}
            {plan.map((placed) =>
              placed.piece.kind === 'clip' && total > 0 ? (
                <div
                  key={pieceId(placed.piece)}
                  className="absolute top-1/2 h-1 -translate-y-1/2 rounded-full bg-chalk/45"
                  style={{
                    left: `${(placed.outStart / total) * 100}%`,
                    width: `${((placed.outEnd - placed.outStart) / total) * 100}%`,
                  }}
                />
              ) : null,
            )}
            <div
              className="brand-fill absolute left-0 top-1/2 h-1 -translate-y-1/2 rounded-full"
              style={{ width: `${progressPercent}%` }}
            />
            <input
              type="range"
              min={0}
              max={total || 0}
              step={0.1}
              value={outTime}
              onChange={onScrub}
              aria-label="Seek"
              className="absolute inset-0 h-full w-full cursor-pointer appearance-none bg-transparent opacity-0"
            />
          </div>

          <div className="relative flex items-center justify-between gap-2">
            <div className="flex items-center gap-1">
              <ControlButton onClick={toggle} label={playing ? 'Pause' : 'Play'}>
                {playing ? <PauseIcon size={20} /> : <PlayIcon size={20} />}
              </ControlButton>
              <ControlButton onClick={() => seekBy(-5)} label="Back 5 seconds">
                <SkipBackIcon size={20} />
              </ControlButton>
              <ControlButton onClick={() => seekBy(5)} label="Forward 5 seconds">
                <SkipForwardIcon size={20} />
              </ControlButton>
            </div>

            <span
              className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap font-mono text-caption tnum text-chalk"
              data-timecode
            >
              {timecode(outTime)} <span className="text-chalk-faint">/ {timecode(total)}</span>
            </span>

            <div className="flex items-center gap-1">
              {activeSource ? (
                <span className="mr-1 hidden max-w-[16rem] truncate text-caption text-chalk-dim sm:inline">
                  Clip from {activeSource.title}
                </span>
              ) : null}
              <ControlButton onClick={toggleFullscreen} label={fullscreen ? 'Exit full screen' : 'Full screen'}>
                {fullscreen ? <ExitFullscreenIcon size={20} /> : <FullscreenIcon size={20} />}
              </ControlButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ControlButton({
  onClick,
  label,
  children,
}: {
  onClick: () => void;
  label: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="inline-flex h-9 w-9 items-center justify-center rounded text-chalk transition-colors duration-120 hover:bg-chalk/10"
    >
      {children}
    </button>
  );
}
